import { parseTransactionIds } from "helpers/transactionIdFilter";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import ActionBtn from "components/buttons/ActionBtn";
import RewardBonusCell from "components/rewardBonus/RewardBonusCell";
import EmptyState from "components/EmptyState/EmptyState";
import Pagination from "components/Pagination";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import { Table } from "components/table/Table";
import {
  compensationsListLimit,
  compensationsListLimits,
} from "configs/limits";
import { ModalTypes } from "configs/types";
import { resolveRewardBonusDisplay } from "helpers/rewardBonusDisplay";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import { useAppDispatch, useAppSelector } from "store/index";
import { openModal } from "store/modals/slice";
import {
  CompensationOrder,
  getCompensationsList,
  handlePaginate,
  resetCompensationsList,
} from "store/payments/compensations/slice";
import CompensationsFilters, {
  FilterOption,
  toIsoDateTime,
} from "./components/CompensationsFilters";
import { RefreshButton } from "components/buttons/RefreshButton";

const TYPE_LABELS: Record<string, string> = {
  compensation: "Compensation",
  reward: "Reward",
  other_credit: "Other Credit",
  balance_transfer_credit: "Balance Transfer Credit",
  adjustment: "Adjustment",
  regular: "Regular",
  promo: "Promo",
};

const displayValue = (value?: string | number | null) =>
  value === undefined || value === null || value === "" ? "—" : value;

const truncateValue = (value: string, maxLength = 25) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;

const CompensationsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = usePermissionHelper();
  const canRead = permissions?.read ?? false;
  const canCreate = permissions?.create ?? false;
  const [order, setOrder] = useState<CompensationOrder>({ id: "desc" });
  const [currentLimit, setCurrentLimit] = useState(compensationsListLimit);
  const [operationId, setOperationId] = useState("");
  const [userIds, setUserIds] = useState("");
  const [transactionCode, setTransactionCode] = useState("");
  const [playerNickname, setPlayerNickname] = useState("");
  const [operators, setOperators] = useState<FilterOption[]>([]);
  const [amountFrom, setAmountFrom] = useState("");
  const [amountTo, setAmountTo] = useState("");
  const [types, setTypes] = useState<FilterOption[]>([]);
  const [statuses, setStatuses] = useState<FilterOption[]>([]);
  const [currencies, setCurrencies] = useState<FilterOption[]>([]);
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");

  const list = useAppSelector((store) => store.paymentsCompensations.list);
  const isListFetched = useAppSelector(
    (store) => store.paymentsCompensations.isListFetched,
  );
  const totalItems = useAppSelector(
    (store) => store.paymentsCompensations.totalItems,
  );
  const totalPages = useAppSelector(
    (store) => store.paymentsCompensations.totalPages,
  );
  const activePage = useAppSelector(
    (store) => store.paymentsCompensations.activePage,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'paymentsCompensations',
    isListFetched
  });

  useEffect(
    () => () => {
      dispatch(resetCompensationsList());
    },
    [dispatch],
  );

  useEffect(() => {
    if (canRead && !isListFetched) {
      dispatch(
        getCompensationsList({
          limit: currentLimit,
          offset: activePage - 1,
          order,
          ...(operationId && { operationId: Number(operationId) }),
          ...(userIds.trim() && {
            userIds: parseTransactionIds(userIds),
          }),
          ...(types.length && {
            compensationTypes: types.map((item) => String(item.value)),
          }),
          ...(transactionCode.trim() && {
            transactionCode: transactionCode.trim(),
          }),
          ...(playerNickname.trim() && {
            playerNickname: playerNickname.trim(),
          }),
          ...(operators.length && {
            operatorIds: operators.map((item) => Number(item.value)),
          }),
          ...(statuses.length && {
            operationStatusCodes: statuses.map((item) => String(item.value)),
          }),
          ...(createdFrom && { createdFrom: toIsoDateTime(createdFrom) }),
          ...(createdTo && { createdTo: toIsoDateTime(createdTo) }),
          ...(currencies.length && {
            currencies: currencies.map((item) => String(item.value)),
          }),
          ...(amountFrom.trim() && { amountFrom: amountFrom.trim() }),
          ...(amountTo.trim() && { amountTo: amountTo.trim() }),
        }),
      );
    }
  }, [activePage, canRead, currentLimit, dispatch, isListFetched, order]);

  const handleOrderClick = () => {
    setOrder((currentOrder) => ({
      id: currentOrder.id === "desc" ? "asc" : "desc",
    }));
    dispatch(handlePaginate(1));
  };

  const handleOpenSelectPlayer = () => {
    if (!canCreate) return;

    navigate(`${location.pathname}?activeModal=${ModalTypes.selectPlayer}`);
    dispatch(openModal({ activeModal: ModalTypes.selectPlayer }));
  };

  const handleOpenBonus = (bonusId: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.bonus}&entityId=${bonusId}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.bonus }));
  };

  const handleApply = () => {
    if ((amountFrom.trim() || amountTo.trim()) && currencies.length !== 1) {
      return;
    }
    dispatch(handlePaginate(1));
  };

  const operatorOptions = useMemo(() => {
    const options = new Map<number, FilterOption>();
    list.forEach((item) => {
      if (item.operatorId != null && item.operatorName) {
        options.set(item.operatorId, {
          value: item.operatorId,
          label: item.operatorName,
        });
      }
    });
    operators.forEach((operator) =>
      options.set(Number(operator.value), operator),
    );
    return Array.from(options.values());
  }, [list, operators]);

  const tableData = list.map((item) => ({
    operationId: item.operationId,
    userId: item.userId,
    playerNickname: displayValue(item.playerNickname),
    playerEmail: displayValue(item.playerEmail),
    type: TYPE_LABELS[item.compensationType] ?? item.compensationType ?? "—",
    amount: `${item.amount} ${item.currency}`,
    wager: item.wagerMultiplier,
    transactionCode: displayValue(item.transactionCode),
    promoOrBonusId: displayValue(item.promoOrBonusId ?? item.bonusId),
    compensationType: item.compensationType,
    bonusId: item.bonusId ?? null,
    bonusTitle: item.bonusTitle ?? null,
    bonusImage: item.bonusImage ?? null,
    admin: displayValue(item.operatorName),
    status: item.statusCode,
    created: item.createdAt,
  }));

  if (!canRead) return null;

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <CompensationsFilters
        operationId={operationId}
        setOperationId={setOperationId}
        userIds={userIds}
        setUserIds={setUserIds}
        transactionCode={transactionCode}
        setTransactionCode={setTransactionCode}
        playerNickname={playerNickname}
        setPlayerNickname={setPlayerNickname}
        selectedOperators={operators}
        setSelectedOperators={setOperators}
        operatorOptions={operatorOptions}
        amountFrom={amountFrom}
        setAmountFrom={setAmountFrom}
        amountTo={amountTo}
        setAmountTo={setAmountTo}
        selectedTypes={types}
        setSelectedTypes={setTypes}
        selectedStatuses={statuses}
        setSelectedStatuses={setStatuses}
        selectedCurrencies={currencies}
        setSelectedCurrencies={setCurrencies}
        createdFrom={createdFrom}
        createdTo={createdTo}
        setCreatedRange={(from, to) => {
          setCreatedFrom(from);
          setCreatedTo(to);
        }}
        handleApply={handleApply}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          {canCreate && (
            <div className="page-list-header--box">
              <ActionBtn
                txt={t("createCompensation")}
                handleClick={handleOpenSelectPlayer}
              />
            </div>
          )}
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={12} rowsCount={12} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap table">
            {list.length === 0 && (
              <EmptyState message={t("noCompensationTransactionsFound")} />
            )}

            {list.length > 0 && (
              <Table
                data={tableData}
                idField="operationId"
                hasCheckbox={false}
                hasOptions={false}
                onSort={(key) => {
                  if (key === "operationId") handleOrderClick();
                }}
                columns={{
                  hiddenColumns: [
                    "compensationType",
                    "bonusId",
                    "bonusTitle",
                    "bonusImage",
                  ],
                  sortingColumns: {
                    operationId: {
                      value: "ID",
                      hasSorting: true,
                      sortedBy: order.id,
                    },
                    userId: { value: "User ID" },
                    playerNickname: { value: "Player Nickname" },
                    playerEmail: { value: "Player Email" },
                    type: { value: "Type" },
                    amount: { value: "Amount" },
                    wager: { value: "Wager" },
                    transactionCode: { value: "Transaction code" },
                    promoOrBonusId: { value: "Promo / Bonus ID" },
                    admin: { value: "Admin" },
                    status: { value: "Status" },
                    created: { value: "Created" },
                  },
                }}
                customColumns={(row) => ({
                  playerEmail: (
                    <span title={String(row.playerEmail)}>
                      {truncateValue(String(row.playerEmail), 33)}
                    </span>
                  ),
                  transactionCode: (
                    <span title={String(row.transactionCode)}>
                      {truncateValue(String(row.transactionCode))}
                    </span>
                  ),
                  promoOrBonusId:
                    resolveRewardBonusDisplay(row).mode === "plain" ? (
                      <span title={String(row.promoOrBonusId)}>
                        {truncateValue(String(row.promoOrBonusId))}
                      </span>
                    ) : (
                      <RewardBonusCell
                        compensationType={row.compensationType}
                        bonusId={row.bonusId}
                        bonusTitle={row.bonusTitle}
                        bonusImage={row.bonusImage}
                        onOpen={handleOpenBonus}
                      />
                    ),
                })}
              />
            )}
          </div>
        )}

        {isListFetched && list.length > 0 && (
          <Pagination
            limitsArray={compensationsListLimits}
            handleLimitClick={(limit) => {
              setCurrentLimit(limit);
              dispatch(handlePaginate(1));
            }}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handlePrevClick={() => {
              if (activePage > 1) dispatch(handlePaginate(activePage - 1));
            }}
            handleNextClick={() => {
              if (activePage < totalPages) {
                dispatch(handlePaginate(activePage + 1));
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CompensationsList;
